import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

const styles = {
    container: { maxWidth: '1100px', margin: '0 auto', padding: '32px 24px', color: '#e0e0e0' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' },
    title: { fontSize: '28px', fontWeight: 700, color: '#ffffff', margin: 0 },
    addButton: {
        backgroundColor: '#bb86fc', color: '#000', border: 'none', borderRadius: '6px',
        padding: '10px 20px', fontWeight: 600, cursor: 'pointer', textDecoration: 'none',
        fontSize: '14px'
    },
    filterRow: { display: 'flex', gap: '10px', marginBottom: '24px', alignItems: 'center' },
    searchInput: {
        flex: 4, padding: '10px 14px', backgroundColor: '#2a2a2a', border: '1px solid #444',
        borderRadius: '6px', color: '#e0e0e0', fontSize: '15px', boxSizing: 'border-box'
    },
    verifiedSelect: { flex: 1, backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', cursor: 'pointer', minWidth: '0' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '10px 14px', borderBottom: '2px solid #333', color: '#aaa', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
    td: { padding: '12px 14px', borderBottom: '1px solid #2a2a2a', fontSize: '15px', verticalAlign: 'middle' },
    link: { color: '#bb86fc', textDecoration: 'none', fontWeight: 500 },
    avatar: { width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', marginRight: '10px', verticalAlign: 'middle' },
    avatarPlaceholder: {
        width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#333',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: '#bb86fc', fontWeight: 700, fontSize: '15px', marginRight: '10px', verticalAlign: 'middle'
    },
    nameCell: { display: 'flex', alignItems: 'center' },
    emptyState: { textAlign: 'center', padding: '60px 0', color: '#666' },
    badge: {
        display: 'inline-block', backgroundColor: '#2a2a2a', color: '#aaa',
        borderRadius: '12px', padding: '2px 10px', fontSize: '13px'
    }
};

export default function MemberList() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterVerified, setFilterVerified] = useState('');

    useEffect(() => {
        axios.get(`${config.API_URL}/members`)
            .then(res => setMembers(res.data.members || []))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, []);

    const filtered = members.filter(m => {
        if (search && !(normalizeStr(m.name).includes(normalizeStr(search)) ||
            (m.hometown_city && normalizeStr(m.hometown_city).includes(normalizeStr(search))))) return false;
        if (filterVerified === 'verified' && !m.sharc_id) return false;
        if (filterVerified === 'unverified' && m.sharc_id) return false;
        return true;
    });

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Members</h1>
                <Link to="/add-member" style={styles.addButton}>+ Add Member</Link>
            </div>

            <div style={styles.filterRow}>
                <input
                    style={styles.searchInput}
                    placeholder="Search members..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <select
                    value={filterVerified}
                    onChange={e => setFilterVerified(e.target.value)}
                    style={styles.verifiedSelect}
                >
                    <option value="">All</option>
                    <option value="verified">Verified</option>
                    <option value="unverified">Unverified</option>
                </select>
            </div>

            {loading ? (
                <div style={styles.emptyState}>Loading...</div>
            ) : filtered.length === 0 ? (
                <div style={styles.emptyState}>
                    {(search || filterVerified) ? 'No members match your search.' : 'No members added yet.'}
                </div>
            ) : (
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Name</th>
                            <th style={styles.th}>Hometown</th>
                            <th style={styles.th}>Artists</th>
                            <th style={styles.th}>Shows</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(m => (
                            <tr key={m.id}>
                                <td style={styles.td}>
                                    <div style={styles.nameCell}>
                                        {m.image_url
                                            ? <img src={m.image_url} alt={m.name} style={styles.avatar} />
                                            : <span style={styles.avatarPlaceholder}>{m.name.charAt(0).toUpperCase()}</span>
                                        }
                                        <Link to={`/members/${m.slug}`} style={styles.link}>{m.name}</Link>
                                    </div>
                                </td>
                                <td style={styles.td}>
                                    {[m.hometown_city, m.hometown_state, m.hometown_country].filter(Boolean).join(', ') || <span style={{ color: '#555' }}>—</span>}
                                </td>
                                <td style={styles.td}>
                                    <span style={styles.badge}>{m.artist_count || 0}</span>
                                </td>
                                <td style={styles.td}>
                                    <span style={styles.badge}>{m.show_count || 0}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
